<template>
  <b-container fluid>
    <b-alert v-if="alert.message != ''" :variant="alert.variant">{{alert.message}}</b-alert>
    <h1>Product Pricing Sums</h1>
    <b-row align-h="center">
      <b-form @submit="search" inline>
        <b-form-textarea placeholder="Product/Part Number (one per line)" v-model="bomKitNumber" class="mx-1" />
        <b-btn variant="primary" type="submit" class="mx-1">Search BOMs/KITs</b-btn>
        <b-btn variant=success class=my-1 v-if="items.length>0 && !busy" @click=exportData>Export</b-btn>
      </b-form>
    </b-row>
    <fingerprint-spinner v-if="busy" :animation-duration="1500" :size="300" color="#293a93" class="my-3" />
    <section v-else class="mx-2">
      <b-row class="my-1">
        <table class="table fixed-table" id=data>
          <thead>
            <tr>
              <th>Product Number</th>
              <th>Product Description</th>
              <th>Part Type</th>
              <th>Product Price (FB)</th>
              <th>Product Price (WA)</th>
              <th>Sum of Costs</th>
              <th>
                <b-form-checkbox switch v-model=topLevel />
                {{ this.topLevel ? "Top Level Price" : "Raw Material Price" }}
              </th>
              <th>Product Price %</th>
              <th>On Sale?</th>
              <th>Sale Price</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.product_id">
              <td>
                {{item.product_num}}
                <b-btn small class='px-1 py-0 mb-1' variant=primary v-if="isAssembly(item)" :href="`/inventory/bom-kit?bomKitNumber=${item.product_num}`" target="_blank">BOM/KIT</b-btn>
                <b-btn small class='px-1 py-0 mb-1' variant=primary target=_blank :href="`/inventory/qty-calculations?productNum=${item.product_num}`">Inventory</b-btn>
              </td>
              <td>{{item.product_desc}}</td>
              <td>{{item.item_type}}</td>
              <td><span>{{item.price | formatDollars }}</span></td>
              <td>
                <span v-if="item.saleInfo">{{item.saleInfo.regularPrice | formatDollars}}</span>
                <span v-else>--</span>
              </td>
              <td v-if="item.priceInfo">{{item.sumOfCost | formatDollars}}</td>
              <td v-if="item.priceInfo">
                <div v-if="isAssembly(item)">
                  <span>{{getSumOfPrice(item) | formatDollars}}</span>
                  <b-btn small class='px-1 py-0 mb-1' :variant="getPriceButtonVariant(item)" @click="showPriceBreakdown(item)">Prices</b-btn>
                </div>
                <span v-else>--</span>
              </td>
              <td v-if="item.priceInfo">
                <span v-if="isAssembly(item)">{{Math.round(item.price / getSumOfPrice(item) * 100)}}%</span>
                <span v-else>--</span>
              </td>
              <td><span v-if="item.saleInfo">{{item.saleInfo.onSale ? "✅" : "❌"}}</span><span v-else>--</span></td>
              <td><span v-if="item.saleInfo">{{item.saleInfo.salePrice | formatDollars}}</span><span v-else>--</span></td>
            </tr>
          </tbody>
        </table>
      </b-row>
    </section>
    <b-modal ref=priceDetail size=lg hide-header hide-footer>
      If a Part Num shows up multiple times here, it just means that the KIT/BOM being broken down uses that item multiple times, sometimes in different quantities
      <b-table :fields=detailColumns :items=priceRow class='mt-2'>
        <template slot=qty slot-scope=row>
          {{row.item.qty | formatNumber}}
        </template>
        <template slot=price slot-scope=row>
          <span v-if="!Number(row.item.price) && isInventory(row.item)" style="background-color:red;">
            {{row.item.price | formatDollars}}
          </span>
          <span v-else>
            {{ row.item.price | formatDollars }}
          </span>
        </template>
        <template slot=cost slot-scope=row>
          <span v-if="!Number(row.item.cost) && isInventory(row.item)" style="background-color:red;">
            {{ row.item.cost | formatDollars }}
          </span>
          <span v-else>
            {{ row.item.cost | formatDollars }}
          </span>
        </template>
        <template slot=totalPrice slot-scope=row>
          <span v-if="!Number(row.item.price) && isInventory(row.item)" style="background-color:red;">
            {{row.item.price * row.item.qty | formatDollars}}
          </span>
          <span v-else>
            {{ row.item.price * row.item.qty | formatDollars }}
          </span>
        </template>
        <template slot=totalCost slot-scope=row>
          <span v-if="!Number(row.item.cost) && isInventory(row.item)" style="background-color:red;">
            {{ row.item.cost * row.item.qty | formatDollars }}
          </span>
          <span v-else>
            {{ row.item.cost * row.item.qty | formatDollars }}
          </span>
        </template>
      </b-table>
    </b-modal>
  </b-container>
</template>

<script>
  import axios from 'axios';
  import NiceDate from '../utility/NiceDate';
  import { FingerprintSpinner } from 'epic-spinners';
  import utility from '../../helpers/utility';
  const BASE_API = process.env.VUE_API_ROOT;

  export default {
    components: {
      NiceDate,
      FingerprintSpinner
    },
    data() {
      return {
        alert: {
          variant: 'danger',
          message: '',
        },
        bomKitNumber: this.$route.query.bomKitNumber,
        busy: false,
        topLevel: true,
        items: [],
        totalRows: 0,
        pricingInfo: {},
        topPricingInfo: {},
        saleInfo: {},

        detailColumns: [],
        priceRow: [],
      }
    },
    mounted() {
      if (this.bomKitNumber) {
        this.search();
      }
    },
    methods: {
      async search(evt) {
        if (evt) evt.preventDefault();
        this.alert.message = '';
        this.busy = true;
        this.items = [ ]

        Promise.all(this.bomKitNumber.split(/[\n,]+/).map(async num => {
          return axios({
            method: 'post',
            headers: { 'Content-Type': 'text/plain' },
            url: `${BASE_API}/ams3-fishioninfo/part-info/search`,
            data: { productNum: num.replace(/^\s+|\s+$/gm,''), exactMatch: true },
          }).then(async (response) => {
            let items = await this.getItems(response)
            return Promise.all([ this.loadItems(items), this.fetchTopLevelPricing(items)])
          }).catch(error => {
            console.error('Failed to get top level product', error)
          })
        })).finally(() => {
          this.items.forEach(item => {
            item.saleInfo = this.saleInfo[item.key]
            item.priceInfo = this.pricingInfo[item.key]
            item.topPriceInfo = this.topPricingInfo[item.key]

            if (!!item.priceInfo) {
              item.rawSumOfPrice = item.priceInfo.reduce((m,i) => m+i.cost, 0)
              item.sumOfCost = item.priceInfo.reduce((m,i) => m+i.cost, 0)
            }

            if (!!item.topPriceInfo) {
              item.topSumOfPrice = item.topPriceInfo.reduce((m,i) => m+i.price, 0)
            }
          })

          this.busy = false;
        })
      },
      async getItems(response) {
        response.data

        let localItems = response.data;
        await Promise.all(localItems.map(async item => {
          return axios.get(`${BASE_API}/fishbowl/product/${item.product_num}`).then(response => {
            if (response.data.length > 0) {
              item.price = Number(response.data[0].price)
            }
          })
        }))
        localItems = localItems.filter(i => typeof(i.price) === 'number')
        this.items.push(...localItems)
        if (this.totalRows === 0) {
          this.alert.variant = 'warning';
          this.alert.message = 'No BOMs/KITs Found';
        }
        return localItems
      },
      async loadItems(localItems) {
        return Promise.all(localItems.map(async (item) => {
          let num = item.product_num === "" ? item.part_num : item.product_num;
          item.key = `${item.part_id}|${item.product_id}`

          return axios({
            method: 'post',
            headers: { 'Content-Type': 'text/plain' },
            url: `${BASE_API}/ams3-fishioninfo/part-info/search`,
            data: { productNum: num, exactMatch: true }
          }).then(async (response) => {
            let subitems = response.data[0].lowest_parts.split(';').filter(subitem => subitem.length > 0).map(subitem => {
              let splits = subitem.split(',')
              return { num: splits[0].replaceAll('*', ''), qty: Number(splits[2]) }
            })
            await Promise.all([this.setPricingInfo(item, subitems), this.pullSalePrice(item)])
          }).catch(error => {
            console.error('Failed to get BOM/KIT items', error)
          })
        }))
      },
      async fetchTopLevelPricing(items) {
        return Promise.all(items.map(item => axios({
          method: 'post',
          headers: { 'Content-Type': 'text/plain' },
          url: `${BASE_API}/fishbowl/bom-kit/search`,
          data: { bomKitNumber: !item.product_num ? item.part_num : item.product_num }
        }).then(response => {
          response.data.filter(x => (!x.partnum ? x.productnum : x.partnum) === (!item.product_num ? item.part_num : item.product_num)).forEach(bomKit => {
            if (bomKit.parttype == "KIT") {
              axios.get(`${BASE_API}/fishbowl/kit/${bomKit.productid}`).then((response) => {
                this.$set(this.topPricingInfo, item.key, response.data.filter(i => i.kititemtypeid === 10).map(i => ({ num: i.kititemnum, qty: i.quantity, price: i.quantity * (i.prodprice || 0) })))
              })
            } else if (bomKit.parttype == "BOM") {
              axios.get(`${BASE_API}/fishbowl/bom/${bomKit.bomid}`).then((response) => {
                this.$set(this.topPricingInfo, item.key, response.data.filter(i => i.bomitemtypeid === 20).map(i => ({ num: i.bomitemnum, qty: i.quantity, price: i.quantity * (i.prodprice || 0), inventory: i.parttypeid === 10 })))
              })
            }
          })
        })))
      },
      async setPricingInfo(item, subitems) {
        if (typeof(subitems[0]) === 'undefined') {
          return axios({
            method: 'post',
            headers: { 'Content-Type': 'text/plain' },
            url: `${BASE_API}/fishbowl/parts/search-info`,
            data: { num: item.product_num }
          }).then(r => {
            this.$set(this.pricingInfo, item.key, [{
              num: item.product_num,
              qty: 1,
              price: r.data[0].price,
              cost: r.data[0].partAvgCost
            }])
          })
        } else {
          return axios({
            method: 'post',
            headers: { 'Content-Type': 'text/plain' },
            url: `${BASE_API}/fishbowl/parts/bulk-search-info`,
            data: [ subitems.map(si => si.num) ]
          }).then(response => {
            let values = subitems.map(si => {
              let match = response.data.filter(x => x.num == si.num)[0]
              return { num: si.num, qty: si.qty, price: Number(match.price) * si.qty, cost: Number(match.partAvgCost) * si.qty }
            })
            this.$set(this.pricingInfo, item.key, values)
          })
        }
      },
      async pullSalePrice(item) {
        return axios.get(`${BASE_API}/workarea/pricing/${item.product_num}`).then(response => {
          let data = JSON.parse(response.data)
          if (!data.problem) {
            let x = data.pricing_sku.prices.filter(x => x.min_quantity == 1)[0]
            this.$set(this.saleInfo, item.key, { regularPrice: x.regular.cents / 100, onSale: x.on_sale, salePrice: x.sale.cents / 100 })
          }
        })
      },
      getSumOfPrice(item) {
        var priceList = (this.topLevel ? item.topPriceInfo : item.priceInfo)
        if (!priceList) return '---'
        else return priceList.reduce((m,i) => m + i.price , 0)
      },
      getPriceButtonVariant(item) {
        return (this.topLevel ? item.topPriceInfo : item.priceInfo).some(x => !Number(x.price) && this.isInventory(item)) ? 'danger' : 'primary'
      },
      isInventory(item) {
        return item.inventory !== false
      },
      showPriceBreakdown(item) {
        this.priceRow = this.topLevel ? item.topPriceInfo : item.priceInfo
        this.detailColumns = (this.topLevel ? [
          { key: 'num', label: "Part Num", sortable: true },
          { key: 'qty', sortable: true },
          { key: 'price', sortable: true },
          { key: 'totalPrice', sortable: true },
        ] : [
          { key: 'num', label: "Part Num", sortable: true },
          { key: 'qty', sortable: true },
          { key: 'price', sortable: true },
          { key: 'cost', sortable: true },
          { key: 'totalPrice', sortable: true },
          { key: 'totalCost', sortable: true },
        ])
        this.$refs.priceDetail.show()
      },
      onFilter(items) {
        this.totalRows = items.length;
        this.currentPage = 1;
      },
      isAssembly(item) {
        return item.item_type === 'Bom' || item.item_type === 'Kit'
      },
      exportData() {
        var csv = [[
          "Product Number",
          "Product Description",
          "Part Type",
          "Product Price (FB)",
          "Product Price (WA)",
          "Sum of Costs",
          `${this.topLevel ? "Top Level Price" : "Raw Material Price"}`,
          "Product Price %",
          "On Sale?",
          "Sale Price"
        ].join(',')]
        this.items.forEach(item => {
          csv.push([
            item.product_num,
            `"${item.product_desc.replace(/(\r\n|\n|\r|\s+|\t|&nbsp;)/gm,' ').replace('"', '""').replace(/ +(?= )/g,'')}"`,
            item.item_type,
            utility.betterRound(item.price, 2),
            item.saleInfo ? utility.betterRound(item.saleInfo.regularPrice, 2) : '---',
            utility.betterRound(Number(item.sumOfCost), 2),
            isNaN(this.getSumOfPrice(item)) ? '---' : utility.betterRound(this.getSumOfPrice(item), 2),
            isNaN(this.getSumOfPrice(item)) ? '---' : utility.betterRound(item.price / this.getSumOfPrice(item), 2),
            item.saleInfo ? item.saleInfo.onSale : '---',
            item.saleInfo ? utility.betterRound(item.saleInfo.salePrice, 2) : '---'
          ].join(','))
        })
        var csvString = csv.join('\n');

        var filename = 'export_pricing_' + new Date().toLocaleDateString() + '.csv';
        var link = document.createElement('a');
        link.style.display = 'none';
        link.setAttribute('target', '_blank');
        link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  }
  </script>

  <style scoped>
  .table th {
    background: #007bff;
    color: #fff;
  }
  .table tr {
    background-color: rgba(90, 90, 90, 0.1);
  }
  .table thead th {
    text-align: left !important;
  }
  </style>



// WEBPACK FOOTER //
// src/components/inventory/ProductPricingSum.vue